import { useState, Fragment } from "react";
import { motion, AnimatePresence } from "motion/react";
import { TickerText } from "../components/TickerText";
import ReactMarkdown from "react-markdown";
import { useResponsive } from "../utils/responsiveContext";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

export const About = () => {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [clickedItem, setClickedItem] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const { isMobile } = useResponsive();
  const handleMouseMove = (e, itemId) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
    setHoveredItem(itemId);
  };

  const handleMouseLeave = () => {
    setHoveredItem(null);
  };

  const items = [
    {
      id: "01",
      text: "Full Stack Developer",
      size: "sm:text-3xl md:text-5xl lg:text-8xl text-2xl",
      description:
        "I love building scalable, real-time apps that actually work under pressure. From bidding platforms to multiplayer games, I’ve stitched together React.js, Next.js, TypeScript, Redux Toolkit, NestJS, Node.js, Express.js, and MongoDB into production-grade systems. I make WebSockets, Socket.IO, Redis Pub/Sub, and WebRTC play nicely so users get low-latency, real-time experiences, and I optimize backends with sharding, caching, and indexing to keep things snappy no matter the traffic.",
      skills: [
        "React.js",
        "Next.js",
        "TypeScript",
        "Redux Toolkit",
        "Tailwind CSS",
        "Chakra UI",
        "Material UI",
        "Node.js",
        "Express.js",
        "NestJS",
        "REST APIs",
        "WebSocket Gateways",
        "Socket.IO",
        "WebRTC",
        "Redis Pub/Sub",
        "MongoDB",
        "PostgreSQL",
        "Caching",
        "Sharding",
        "Database Optimization",
      ],
    },
    {
      id: "02",
      text: "GenAI Developer",
      size: "sm:text-3xl md:text-5xl lg:text-8xl text-2xl",
      description:
        "I’ve been diving deep into AI-powered systems using LangChain, Llama 3.3 70B, RAG workflows, Pinecone, and OpenAI embeddings. From real-time AI agents in games to smart customer support systems, I build context-aware pipelines that respond fast and intelligently. I handle session management, vector retrieval, and inference optimization so the AI feels alive, reliable, and ready to interact at scale.",
      skills: [
        "LangChain",
        "Generative AI",
        "OpenAI Models",
        "Llama 3.3 70B",
        "RAG",
        "OpenAI Embeddings",
        "Vector Databases",
        "Context-aware Systems",
        "Session Persistence",
        "Real-time Analytics",
      ],
    },
    {
      id: "03",
      text: "DevOps Specialist",
      size: "sm:text-3xl md:text-5xl lg:text-8xl text-2xl",
      description:
        "Deploying apps at scale is my jam. I work with AWS (ECS, EKS, Lambda, S3, SQS), Docker, Kubernetes, Jenkins, and GitHub CI/CD to make sure every service runs smooth, secure, and zero-downtime. I also keep an eye on everything with Prometheus, Grafana, and Filebeat, making sure our systems stay fast, observable, and fault-tolerant while teams focus on building features.",
      skills: [
        "AWS EC2",
        "AWS ECS",
        "AWS EKS",
        "AWS Lambda",
        "AWS S3",
        "AWS SQS",
        "Docker",
        "Kubernetes",
        "GitHub CI/CD",
        "Jenkins",
        "Prometheus",
        "Grafana",
        "Filebeat",
        "Zero-downtime Deployments",
        "Observability",
      ],
    },
  ];

  return (
    <div className="flex md:justify-center justify-start items-start flex-col md:flex-row md:my-8 my-2 h-full p-4">
      <div className="md:w-[35%] w-full">
        <p className="sm:text-3xl md:text-5xl lg:text-8xl text-4xl text-center font-light p-6">
          WHAT I DO
        </p>
      </div>
      <div className="md:w-[65%] w-full h-[75%] flex flex-col gap-4 py-2 justify-between">
        <div
          key={"00"}
          className="sm:text-lg md:w-[80%] w-full text-xs text-justify font-mono italic bg-shade-1 text-shade-4 p-6 rounded-2xl border border-shade-2 shadow-xl select-none"
        >
          <ReactMarkdown
            components={{
              strong: ({ children }) => (
                <span className="text-main font-normal">{children}</span>
              ),
              p: ({ children }) => <p className="leading-10">{children}</p>,
            }}
          >
            {`Hey! I'm **Anurag**, a Full-Stack Engineer who's built **5+ production-grade apps**. I create **scalable backends** with **Node.js / NestJS**, **interactive frontends** with **React / Next.js**, and **real-time systems** with **WebSockets, Redis Pub/Sub, WebRTC**. I also integrate **AI/LLMs** like **LangChain, OpenAI models, Llama 3.3 70B**, deploy on **AWS / Docker / Kubernetes**, and geek out on building **fast, reliable, user-friendly products**.`}
          </ReactMarkdown>
        </div>
        {items.map((item) => (
          <Fragment key={item.id}>
            <div
              className="relative flex"
              onMouseMove={(e) => handleMouseMove(e, item.id)}
              onMouseLeave={handleMouseLeave}
              onClick={() =>
                setClickedItem((prev) => (prev === item.id ? null : item.id))
              }
            >
              <span className="sm:text-4xl text-lg p-2 flex items-center justify-center opacity-50 mr-2">
                {item.id}
              </span>
              <div
                className={`${item.size} p-2 text-left font-lightrounded-xl cursor-pointer flex w-full`}
              >
                <TickerText text={item.text} skills={item.skills.join(" * ")} />
              </div>
              {!isMobile && (
                <AnimatePresence>
                  {hoveredItem === item.id && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                      transition={{
                        type: "spring",
                        damping: 25,
                        stiffness: 300,
                        ease: [0.25, 0.1, 0.25, 1],
                      }}
                      style={{
                        left: mousePosition.x,
                        top: mousePosition.y,
                        x: "-50%",
                        y: "-50%",
                        willChange: "transform, opacity",
                        transform: "translateZ(0)",
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                      }}
                      className="absolute pointer-events-none z-50"
                    >
                      <motion.div
                        animate={{
                          x: 0,
                          y: 0,
                        }}
                        transition={{
                          type: "spring",
                          damping: 30,
                          stiffness: 400,
                          mass: 0.5,
                          ease: [0.25, 0.1, 0.25, 1],
                        }}
                        style={{
                          willChange: "transform",
                          transform: "translateZ(0)",
                          backfaceVisibility: "hidden",
                          WebkitBackfaceVisibility: "hidden",
                        }}
                        className="aspect-video w-48 sm:w-96 rounded-xl shadow-2xl flex items-center justify-center p-6 bg-shade-2 opacity-90"
                      >
                        <p className="text-shade-4 text-justify text-lg sm:text-xl font-medium opacity-100">
                          {item.description}
                        </p>
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
            {isMobile && (
              <Dialog
                open={clickedItem === item.id}
                onClose={() => setClickedItem(null)}
                PaperProps={{
                  style: {
                    borderRadius: 10,
                    backgroundColor: "var(--shade-1)",
                  },
                }}
              >
                <DialogContent className="bg-shade-1  p-0">
                  <p className="w-full h-full font-mono italic text-shade-4 text-justify">
                    {item.description}
                  </p>
                </DialogContent>
                <DialogActions className="bg-shade-1">
                  <Button
                    className="!text-shade-4 !px-3 !py-2 !rounded-xl !border !border-shade-4"
                    variant="outlined"
                    size={isMobile ? "small" : "medium"}
                    onClick={() => setClickedItem(null)}
                  >
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            )}
          </Fragment>
        ))}
      </div>
    </div>
  );
};
